import React from 'react';
import { Button, ClickAwayListener, Fade, IconButton, Popper, Typography } from "@mui/material";
import {Block, Delete, Edit, MoreVert, Visibility} from "@mui/icons-material";
import {ApproveIcon} from "../../../../../utils/icons";
import {MoreOptsMenu, IconContainer} from "../../../../../theme";
import Auth from "../../../../../auth";

// region TypeScript Interfaces
interface CatalogTableMoreOptsProps {
    isRetailer: boolean;
    currentCatalog: string;
    row: any;
    history: any;
    setApproveDialogOpen: (open: boolean) => void;
    setRejectDialogOpen: (open: boolean) => void;
    setDeleteDialogOpen: (open: boolean) => void;
    setSelectedRow: (row: any) => void;
    updateListing: (row: any, status: string) => void;
    // catalog_results passes many additional pass-through props (table, config,
    // setConfig, setUpdating, etc.) — accept them as untyped extras.
    [key: string]: unknown;
}
// endregion

const iconButtonSx = {
    width: "100%",
    justifyContent: "flex-start",
    padding: "12px",
    textTransform: "none",
    borderRadius: "6px",
    border: "none",
    background: "#fff",
    boxShadow: "0px 2px 4px -1px rgba(10, 9, 11, 0.02), 0px 5px 13px -5px rgba(10, 9, 11, 0.05)",
    cursor: "pointer",
    "&.Mui-disabled": {
        border: "none",
    },
};

function CatalogTableMoreOpts(props: CatalogTableMoreOptsProps) {
    const {isRetailer, currentCatalog} = props;

    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");
    const canEdit =
        (isRetailer && hasInventoryManagePermission) || (!isRetailer && currentCatalog === "mycatalog");

    const {setApproveDialogOpen, setRejectDialogOpen, setDeleteDialogOpen} = props;
    const {setSelectedRow, updateListing} = props;

    // Simple local state - each menu manages itself
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleMenuToggle = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (open) {
            setAnchorEl(null);
        } else {
            setAnchorEl(e.currentTarget);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton
                data-testid={`poc-catalog-row-actions-${props.row.sku}`}
                onClick={handleMenuToggle}
                style={{borderRadius: 100}}
                size="large">
                <MoreVert/>
            </IconButton>
            <Popper open={open} anchorEl={anchorEl} transition placement={"left-start"}
                    style={{zIndex: 1300}}
            >
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={handleMenuClose} mouseEvent="onMouseDown">
                        <Fade {...TransitionProps} timeout={350}>
                            <MoreOptsMenu>

                                {canEdit && isRetailer && (
                                    <>
                                        {props.currentCatalog === "partnercatalog" ? (
                                            <>
                                                <Button
                                                    sx={iconButtonSx}
                                                    disabled={props.row.approvalStatus === "Approved" || !canEdit}
                                                    onClick={(e) => {
                                                        setApproveDialogOpen(true);
                                                        setSelectedRow(props.row);
                                                    }}>
                                                    <IconContainer>
                                                        <ApproveIcon style={{width: "20px", height: "20px"}}/>
                                                        <Typography>Approve</Typography>
                                                    </IconContainer>
                                                </Button>

                                                <Button
                                                    sx={iconButtonSx}
                                                    disabled={!canEdit}
                                                    onClick={(e) => {
                                                        setRejectDialogOpen(true);
                                                        setSelectedRow(props.row);
                                                    }}>
                                                    <IconContainer>
                                                        <Block sx={{ fontSize: "20px" }}/>
                                                        <Typography>Reject</Typography>
                                                    </IconContainer>
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    sx={iconButtonSx}
                                                    disabled={!canEdit || props.row.syncStatus === "Listing Pending" || props.row.syncStatus === "Listed"}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        updateListing(props.row, "Listing Pending")
                                                    }}
                                                >
                                                    <IconContainer>
                                                        <ApproveIcon
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                                ...(props.row.syncStatus === "Listing Pending" || props.row.syncStatus === "Listed" ? {opacity: 0.5} : {})
                                                            }}/>
                                                        <Typography>List</Typography>
                                                    </IconContainer>
                                                </Button>

                                                <Button
                                                    sx={iconButtonSx}
                                                    disabled={!canEdit || props.row.syncStatus === "Delist Pending" || props.row.syncStatus === "Delisted"}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        updateListing(props.row, "Delist Pending")
                                                    }}
                                                >
                                                    <IconContainer>
                                                        <Block
                                                            sx={{
                                                                fontSize: "20px",
                                                                ...(props.row.syncStatus === "Delist Pending" || props.row.syncStatus === "Delisted" ? {opacity: 0.5} : {})
                                                            }}/>
                                                        <Typography>Delist</Typography>
                                                    </IconContainer>
                                                </Button>
                                            </>
                                        )}
                                    </>
                                )}

                                <Button
                                    sx={iconButtonSx}
                                    onClick={() => {
                                        // Encoding the URL twice is necessary to prevent the URL from being decoded by the browser
                                        let partnerId = "";
                                        if(props.isRetailer)
                                            partnerId = props.row.supplierId;
                                        else
                                            partnerId = props.row.merchantId;

                                        let isPartner = (!props.isRetailer || props.currentCatalog === "partnercatalog") ? `&partnerId=${partnerId}` : "";
                                        let encodedProductId = encodeURIComponent(encodeURIComponent(props.row.sku));
                                        props.history.push(`/productonboardingcenter/product?catalog=${props.currentCatalog}&productId=${encodedProductId}` + isPartner)
                                    }}
                                >
                                    <IconContainer>
                                        {canEdit ? (
                                            <Edit style={{width: "20px", height: "20px"}}/>
                                        ) : (
                                            <Visibility style={{width: "20px", height: "20px"}}/>
                                        )}

                                        <Typography>
                                            {canEdit ? "Edit" : "View"}
                                        </Typography>
                                    </IconContainer>
                                </Button>

                                {canEdit && (
                                    <Button
                                        sx={iconButtonSx}
                                        data-testid={`poc-catalog-delete-${props.row.sku}`}
                                        disabled={!canEdit}
                                        onClick={(e) => {
                                            setDeleteDialogOpen(true);
                                            setSelectedRow(props.row);
                                        }}
                                    >
                                        <IconContainer>
                                            <Delete style={{width: "20px", height: "20px"}}/>
                                            <Typography>Delete</Typography>
                                        </IconContainer>
                                    </Button>
                                )}
                            </MoreOptsMenu>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
}

export default CatalogTableMoreOpts;
